import * as R from 'ramda';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import colors from '@atom/styles/colors';
import { DashboardCard } from '@atom/types/dashboard';
import { Schema, SchemasConnection } from '@atom/types/schema';
import schemaUtilities from '@atom/utilities/schemaUtilities';

import CardEmpty from '../commonCardStates/CardEmpty';
import { getCardHeight } from '../dashboardUtilities';

import classes from '../dashboardCard.module.css';

interface Props {
  card: DashboardCard;
  cardData: { schemas: SchemasConnection };
}

const DISPLAY_COUNT_BEFORE_OTHER: number = 9;

const prepOtherType = (schemas: Schema[]): Schema[] => {
  const otherSchemaBase = {
    id: 'other',
    name: 'Other',
    assetCount: 0,
    assetType: '',
    attributes: null,
    attributeGroups: [],
    colorId: 42,
    markerId: 0,
    isMaterial: null,
    budget: null,
    elementCount: 0,
    elements: [],
    locationType: null,
    ciEnabled: null,
    picEnabled: null,
  };
  const otherSchemas = schemas.slice(DISPLAY_COUNT_BEFORE_OTHER);
  const assetCount = otherSchemas.reduce(
    (count, schema) => count + schema.assetCount,
    0,
  );
  return [
    ...schemas.slice(0, DISPLAY_COUNT_BEFORE_OTHER),
    {
      ...otherSchemaBase,
      assetCount,
    },
  ];
};

const CardAssetCount = ({ card, cardData }: Props) => {
  const schemas = R.sort(
    (schema1, schema2) => schema2.assetCount - schema1.assetCount,
    cardData?.schemas?.schemas || [],
  );
  const displaySchemas =
    schemas?.length <= DISPLAY_COUNT_BEFORE_OTHER
      ? schemas
      : prepOtherType(schemas);

  const style = {
    height: getCardHeight(card),
  };

  const formatLegendText = (value: string) => {
    const thisSchema = displaySchemas.find(schema => schema.name === value);
    return `${value}: ${thisSchema.assetCount}`;
  };

  return R.isEmpty(schemas) ? (
    <CardEmpty card={card} />
  ) : (
    <div style={style} className={classes.cardContent}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={displaySchemas as Record<string, any>[]}
            dataKey="assetCount"
            nameKey="name"
            startAngle={90}
            endAngle={-270}
            outerRadius="90%"
            fill={colors.neutral.white}
          >
            {displaySchemas.map((schema: Schema, index: number) => (
              <Cell
                style={{ outline: 'none' }}
                key={index}
                fill={schemaUtilities.getSchemaColorFromColorId(schema.colorId)}
              />
            ))}
          </Pie>
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            iconType="circle"
            itemSorter="dataKey"
            formatter={formatLegendText}
            iconSize={8}
          />
          <Tooltip animationDuration={0} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CardAssetCount;
